/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { jsx, keyframes } from '@emotion/core';
import {
  Button,
  Spin,
  Empty,
  Typography,
  Input,
  Pagination,
  Dropdown,
  Checkbox,
} from 'antd';
import { useIntl } from 'react-intl';
import { useProjectId } from '~src/hooks/useProjectId';
import { useTemplates } from '~src/hooks/api-hooks/useTemplates';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DbProject } from '~src/types/db';
import { TEMPLATE_TYPE } from '~src/types/enums';
import { useMemo, useState, useEffect, Fragment } from 'react';
import IconButton from '~components/_common/IconButton/IconButton';
import * as React from 'react';

const { Title } = Typography;

interface TemplateSelectionStepProps {
  selectedTemplateId: string | null;
  onTemplateSelect: (templateId: string) => void;
  onZipUpload: () => void;
  mode?: 'project-builder' | 'archive' | 'project-builder-folder';
}

// Helper to get template type icon
const getTemplateTypeIcon = (type: number | undefined): any => {
  switch (type) {
    case TEMPLATE_TYPE.CWEB_TEMPLATE:
      return ['fad', 'cubes-stacked'];
    case TEMPLATE_TYPE.CUSTOMER_OWN_TEMPLATE:
      return ['far', 'user-check'];
    case TEMPLATE_TYPE.OTHER_PROJECT:
      return ['far', 'folder-tree'];
    default:
      return ['fad', 'cubes-stacked'];
  }
};

// Helper to get template type label
const getTemplateTypeLabel = (type: number | undefined, intl: any): string => {
  switch (type) {
    case TEMPLATE_TYPE.CWEB_TEMPLATE:
      return intl.formatMessage({ id: 'cweb-template' });
    case TEMPLATE_TYPE.CUSTOMER_OWN_TEMPLATE:
      return intl.formatMessage({ id: 'customer-template' });
    case TEMPLATE_TYPE.OTHER_PROJECT:
      return intl.formatMessage({ id: 'other-project' });
    default:
      return '';
  }
};

// Helper to get template type color
const getTemplateTypeColor = (type: number): string => {
  switch (type) {
    case TEMPLATE_TYPE.CWEB_TEMPLATE:
      return '#2563eb'; // blue
    case TEMPLATE_TYPE.CUSTOMER_OWN_TEMPLATE:
      return '#52c41a'; // green
    case TEMPLATE_TYPE.OTHER_PROJECT:
      return '#faad14'; // orange
    default:
      return '#2563eb';
  }
};

export const TemplateSelectionStep: React.FC<TemplateSelectionStepProps> = ({
  selectedTemplateId,
  onTemplateSelect,
  onZipUpload,
  mode,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();
  const [searchText, setSearchText] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<number[]>([
    TEMPLATE_TYPE.CWEB_TEMPLATE,
    TEMPLATE_TYPE.CUSTOMER_OWN_TEMPLATE,
    TEMPLATE_TYPE.OTHER_PROJECT,
  ]);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(7); // Default 7 items per page

  const templatesQuery = useTemplates({}, { enabled: !!projectId });

  const filteredTemplates = useMemo(() => {
    let filtered = templatesQuery.data || [];

    // Exclude type 2 (HAS_PERMITTED_PROJECT) entirely
    filtered = filtered.filter(
      (t) => t.type !== TEMPLATE_TYPE.HAS_PERMITTED_PROJECT
    );

    // Apply search filter
    if (searchText) {
      filtered = filtered.filter((t) =>
        t.name.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Apply type filter
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(
        (t) => t.type !== undefined && selectedTypes.includes(t.type)
      );
    }

    // Sort by type first, then by name
    filtered.sort((a, b) => {
      const typeA = a.type ?? 999;
      const typeB = b.type ?? 999;
      if (typeA !== typeB) {
        return typeA - typeB;
      }
      return a.name.localeCompare(b.name);
    });

    return filtered;
  }, [templatesQuery.data, searchText, selectedTypes]);

  // Calculate paginated templates
  const paginatedTemplates = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredTemplates.slice(startIndex, endIndex);
  }, [filteredTemplates, currentPage, pageSize]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedTypes]);

  // Calculate if all types are selected
  const allTypesSelected = useMemo(() => {
    const availableTypes = [
      TEMPLATE_TYPE.CWEB_TEMPLATE,
      TEMPLATE_TYPE.CUSTOMER_OWN_TEMPLATE,
      TEMPLATE_TYPE.OTHER_PROJECT,
    ];
    return availableTypes.every((type) => selectedTypes.includes(type));
  }, [selectedTypes]);

  // Calculate indeterminate state
  const indeterminate = useMemo(() => {
    return selectedTypes.length > 0 && !allTypesSelected;
  }, [selectedTypes, allTypesSelected]);

  // Handle select all toggle
  const handleSelectAllTypes = () => {
    if (allTypesSelected) {
      setSelectedTypes([]);
    } else {
      setSelectedTypes([
        TEMPLATE_TYPE.CWEB_TEMPLATE,
        TEMPLATE_TYPE.CUSTOMER_OWN_TEMPLATE,
        TEMPLATE_TYPE.OTHER_PROJECT,
      ]);
    }
  };

  // Handle individual type toggle
  const handleTypeToggle = (type: number) => {
    if (selectedTypes.includes(type)) {
      setSelectedTypes(selectedTypes.filter((t) => t !== type));
    } else {
      setSelectedTypes([...selectedTypes, type]);
    }
  };

  // Get button text for type filter
  const getFilterButtonText = (): string => {
    if (allTypesSelected) {
      return intl.formatMessage({ id: 'all-types' });
    }
    if (selectedTypes.length === 0) {
      return intl.formatMessage({ id: 'template-type-filter' });
    }
    const typeNames = selectedTypes
      .map((type) => getTemplateTypeLabel(type, intl))
      .join(', ');
    if (typeNames.length > 30) {
      return `${selectedTypes.length} ${intl.formatMessage({
        id: 'template-type-filter',
      })}`;
    }
    return typeNames;
  };

  if (templatesQuery.isLoading) {
    return (
      <div css={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div css={styles.container}>
      <div css={styles.templateSection}>
        <div css={styles.titleRow}>
          <Title level={4} css={styles.sectionTitle}>
            {intl.formatMessage({ id: 'select-template' })}
          </Title>
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={['far', 'file-archive']} />}
            onClick={onZipUpload}
            css={styles.zipButton}>
            {intl.formatMessage({ id: 'upload-zip-template' })}
          </Button>
        </div>

        <div css={styles.filterSection}>
          <Input
            prefix={<IconButton iconStyle="far" icon="search" backgroundOff />}
            placeholder={intl.formatMessage({ id: 'search-templates' })}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            css={styles.searchInput}
          />
          <Dropdown
            overlay={
              <div css={styles.dropdownMenu}>
                <div css={styles.menuHeader}>
                  <Checkbox
                    checked={allTypesSelected}
                    indeterminate={indeterminate}
                    onChange={handleSelectAllTypes}>
                    <span css={styles.menuHeaderText}>
                      {intl.formatMessage({ id: 'all-types' })}
                    </span>
                  </Checkbox>
                </div>
                <div css={styles.optionsList}>
                  {[
                    TEMPLATE_TYPE.CWEB_TEMPLATE,
                    TEMPLATE_TYPE.CUSTOMER_OWN_TEMPLATE,
                    TEMPLATE_TYPE.OTHER_PROJECT,
                  ].map((type) => (
                    <div
                      key={type}
                      css={styles.optionItem}
                      onClick={() => handleTypeToggle(type)}>
                      <Checkbox
                        checked={selectedTypes.includes(type)}
                        onChange={() => handleTypeToggle(type)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span css={styles.optionLabel}>
                        {getTemplateTypeLabel(type, intl)}
                      </span>
                      <span
                        css={[
                          styles.colorIndicator,
                          { background: getTemplateTypeColor(type) },
                        ]}
                      />
                    </div>
                  ))}
                </div>
              </div>
            }
            trigger={['click']}
            visible={dropdownVisible}
            onVisibleChange={setDropdownVisible}
            placement="bottomRight">
            <Button
              css={styles.typeFilterButton}
              style={{ display: 'flex', gap: '2px' }}>
              <FontAwesomeIcon
                icon={['far', 'filter-list']}
                style={{ fontSize: '14px' }}
              />
              <span>{getFilterButtonText()}</span>
              <FontAwesomeIcon
                icon={['fas', 'chevron-down']}
                css={styles.chevronIcon}
              />
            </Button>
          </Dropdown>
        </div>

        {!templatesQuery.data || templatesQuery.data.length === 0 ? (
          <Empty
            description={intl.formatMessage({ id: 'no-templates' })}
            css={styles.empty}
          />
        ) : filteredTemplates.length === 0 ? (
          <Empty
            description={intl.formatMessage({
              id: 'no-templates-match-search',
            })}
            css={styles.empty}
          />
        ) : (
          <Fragment>
            <div css={styles.templateGrid}>
              {paginatedTemplates.map((template: DbProject) => {
                console.log('Template:', template.name, 'Type:', template.type);
                return (
                  <Button
                    key={template.id}
                    type="default"
                    onClick={() => onTemplateSelect(String(template.id))}
                    css={[
                      styles.templateButton,
                      template.type === TEMPLATE_TYPE.CWEB_TEMPLATE &&
                        styles.templateButtonPrimary,
                      template.type === TEMPLATE_TYPE.CUSTOMER_OWN_TEMPLATE &&
                        styles.templateButtonSuccess,
                      template.type === TEMPLATE_TYPE.OTHER_PROJECT &&
                        styles.templateButtonWarning,
                      selectedTemplateId === String(template.id) &&
                        styles.templateButtonSelected,
                    ]}>
                    <div css={styles.templateButtonContent}>
                      <FontAwesomeIcon
                        icon={getTemplateTypeIcon(template.type)}
                        css={[
                          styles.templateIcon,
                          template.type === TEMPLATE_TYPE.CWEB_TEMPLATE &&
                            styles.templateIconPrimary,
                          template.type ===
                            TEMPLATE_TYPE.CUSTOMER_OWN_TEMPLATE &&
                            styles.templateIconSuccess,
                          template.type === TEMPLATE_TYPE.OTHER_PROJECT &&
                            styles.templateIconWarning,
                        ]}
                      />
                      <div css={styles.templateInfo}>
                        <span css={styles.templateName}>{template.name}</span>
                      </div>
                      {template.type !== undefined && (
                        <div
                          css={[
                            styles.typeBadge,
                            template.type === TEMPLATE_TYPE.CWEB_TEMPLATE &&
                              styles.typeBadgePrimary,
                            template.type ===
                              TEMPLATE_TYPE.CUSTOMER_OWN_TEMPLATE &&
                              styles.typeBadgeSuccess,
                            template.type === TEMPLATE_TYPE.OTHER_PROJECT &&
                              styles.typeBadgeWarning,
                          ]}>
                          {getTemplateTypeLabel(template.type, intl)}
                        </div>
                      )}
                      {selectedTemplateId === String(template.id) && (
                        <FontAwesomeIcon
                          icon={['fas', 'check-circle']}
                          css={styles.checkIcon}
                        />
                      )}
                    </div>
                  </Button>
                );
              })}
            </div>

            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredTemplates.length}
              onChange={(page, newPageSize) => {
                setCurrentPage(page);
                if (newPageSize && newPageSize !== pageSize) {
                  setPageSize(newPageSize);
                  setCurrentPage(1); // Reset to first page when page size changes
                }
              }}
              showSizeChanger
              pageSizeOptions={['7', '10', '20', '50', '100']}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} templates`
              }
              css={styles.pagination}
            />
          </Fragment>
        )}
      </div>
    </div>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    animation: `${fadeIn} 0.4s ease-out`,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  templateSection: {
    flex: 1,
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    marginBottom: 0,
    color: theme.colors.text.primary,
    fontWeight: 600,
  },
  zipButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: 36,
    borderRadius: 6,
    fontWeight: 500,
    fontSize: 14,
    padding: '0 16px',
  },
  filterSection: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
  },
  searchInput: {
    flex: 1,
    '.ant-input': {
      backgroundColor: theme.colors.backgroundSecondary,
    },
  },
  typeFilterButton: {
    width: '200px',
    height: '40px !important',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 11px',
    border: '1px solid #d9d9d9',
    borderRadius: '2px',
    backgroundColor: `${theme.colors.backgroundSecondary} !important`,
    fontSize: '14px',
    color: '#000000',
    transition: 'all 0.3s',
    lineHeight: 'normal',
    '&:hover': {
      borderColor: '#40a9ff',
    },
    '&:focus': {
      borderColor: '#40a9ff',
      outline: 0,
      boxShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)',
    },
    '& > span': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  chevronIcon: {
    fontSize: 11,
    marginLeft: 4,
    color: '#9ca3af',
  },
  dropdownMenu: {
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    overflow: 'hidden',
    minWidth: 280,
  },
  menuHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid #f3f4f6',
    background: '#f9fafb',
    '& .ant-checkbox-wrapper': {
      marginRight: 0,
    },
  },
  menuHeaderText: {
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginLeft: 8,
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 0',
  },
  optionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 16px',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    userSelect: 'none',
    '&:hover': {
      background: '#f9fafb',
    },
    '& .ant-checkbox-wrapper': {
      marginRight: 8,
    },
  },
  optionLabel: {
    fontSize: 13,
    color: '#4b5563',
    flex: 1,
    fontWeight: 500,
    marginLeft: 8,
  },
  colorIndicator: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  templateGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    maxHeight: 420,
    minHeight: 80,
    overflowY: 'auto',
    paddingRight: 8,
    paddingTop: 8,
    marginBottom: 12,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 0,
    '.ant-pagination-total-text': {
      minWidth: '160px',
      display: 'inline-block',
    },
    '.ant-pagination-item-active': {
      borderColor: theme.colors.primary,
      a: {
        color: theme.colors.primary,
      },
    },
    '.ant-pagination-item:hover': {
      borderColor: theme.colors.primary,
      a: {
        color: theme.colors.primary,
      },
    },
  },
  templateButton: {
    width: '100%',
    height: 'auto !important',
    padding: '12px 16px 12px 12px !important',
    textAlign: 'left',
    transition: 'all 0.2s ease',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '8px !important',
    backgroundColor: '#ffffff !important',
    display: 'flex !important',
    alignItems: 'center !important',
    lineHeight: 'normal !important',
    minHeight: '40px',
    '&:hover': {
      transform: 'translateX(4px)',
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.12)',
      borderColor: `${theme.colors.border} !important`,
    },
    '&:focus': {
      outline: 'none',
    },
  },
  templateButtonSelected: {
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
  },
  templateButtonPrimary: {
    borderLeft: '5px solid #2563eb !important',
    backgroundColor: 'rgba(37, 99, 235, 0.06) !important',
    '&:hover': {
      backgroundColor: 'rgba(37, 99, 235, 0.09) !important',
    },
  },
  templateButtonSuccess: {
    borderLeft: '5px solid #52c41a !important',
    backgroundColor: 'rgba(82, 196, 26, 0.06) !important',
    '&:hover': {
      backgroundColor: 'rgba(82, 196, 26, 0.09) !important',
    },
  },
  templateButtonWarning: {
    borderLeft: '5px solid #faad14 !important',
    backgroundColor: 'rgba(250, 173, 20, 0.06) !important',
    '&:hover': {
      backgroundColor: 'rgba(250, 173, 20, 0.09) !important',
    },
  },
  templateButtonContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: 12,
  },
  templateIcon: {
    fontSize: 24,
    flexShrink: 0,
  },
  templateIconPrimary: {
    color: '#2563eb !important',
  },
  templateIconSuccess: {
    color: '#52c41a !important',
  },
  templateIconWarning: {
    color: '#faad14 !important',
  },
  templateInfo: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    marginLeft: 4,
  },
  templateName: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.colors.text.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  typeBadge: {
    display: 'inline-flex !important',
    alignItems: 'center',
    fontSize: '10px !important',
    fontWeight: 700,
    padding: '3px 8px !important',
    borderRadius: '5px !important',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    flexShrink: 0,
    marginLeft: '10px !important',
  },
  typeBadgePrimary: {
    backgroundColor: '#2563eb !important',
    color: '#ffffff !important',
  },
  typeBadgeSuccess: {
    backgroundColor: '#52c41a !important',
    color: '#ffffff !important',
  },
  typeBadgeWarning: {
    backgroundColor: '#faad14 !important',
    color: '#ffffff !important',
  },
  checkIcon: {
    fontSize: 18,
    color: theme.colors.primary,
    flexShrink: 0,
    marginLeft: 6,
  },
  empty: {
    padding: '40px 0',
  },
});
